import React, { useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

const VitalsTab = ({ data, onChange }) => {
  const [newVital, setNewVital] = useState({
    date: new Date().toISOString().split("T")[0],
    time: new Date().toTimeString().split(" ")[0].substring(0, 5),
    weight: "",
    height: "",
    bmi: "",
    temperature: "",
    bloodPressureSystolic: "",
    bloodPressureDiastolic: "",
    heartRate: "",
    respiratoryRate: "",
    oxygenSaturation: "",
    notes: "",
  });

  const handleAddVital = () => {
    if (
      newVital.weight ||
      newVital.bloodPressureSystolic ||
      newVital.heartRate
    ) {
      // Calculate BMI if weight and height are provided
      let bmi = newVital.bmi;
      if (newVital.weight && newVital.height) {
        const weightKg = parseFloat(newVital.weight) * 0.453592; // lbs to kg
        const heightM = parseFloat(newVital.height) * 0.0254; // inches to meters
        bmi = (weightKg / (heightM * heightM)).toFixed(1);
      }

      onChange([
        ...(data || []),
        {
          ...newVital,
          bmi,
          id: Date.now(),
          timestamp: `${newVital.date} ${newVital.time}`,
        },
      ]);

      // Reset form but keep date/time
      setNewVital({
        date: new Date().toISOString().split("T")[0],
        time: new Date().toTimeString().split(" ")[0].substring(0, 5),
        weight: "",
        height: "",
        bmi: "",
        temperature: "",
        bloodPressureSystolic: "",
        bloodPressureDiastolic: "",
        heartRate: "",
        respiratoryRate: "",
        oxygenSaturation: "",
        notes: "",
      });
    }
  };

  const handleRemoveVital = (id) => {
    onChange((data || []).filter((vital) => vital.id !== id));
  };

  const getBloodPressureStatus = (systolic, diastolic) => {
    if (!systolic || !diastolic) return null;
    const sys = parseInt(systolic);
    const dia = parseInt(diastolic);

    if (sys < 120 && dia < 80) return { label: "Normal", color: "success" };
    if (sys < 130 && dia < 80) return { label: "Elevated", color: "warning" };
    if (sys < 140 || dia < 90)
      return { label: "High (Stage 1)", color: "error" };
    return { label: "High (Stage 2)", color: "error" };
  };

  const getBMIStatus = (bmi) => {
    if (!bmi) return null;
    const value = parseFloat(bmi);

    if (value < 18.5) return { label: "Underweight", color: "warning" };
    if (value < 25) return { label: "Normal", color: "success" };
    if (value < 30) return { label: "Overweight", color: "warning" };
    return { label: "Obese", color: "error" };
  };

  const sortedVitals = (data || []).sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
        Vital Signs
      </Typography>

      {/* Add Vital Signs Form */}
      <Card sx={{ mb: 3, bgcolor: "grey.50" }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Record New Vital Signs
          </Typography>
          <Grid container spacing={2}>
            {/* Date and Time */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={newVital.date}
                onChange={(e) =>
                  setNewVital({ ...newVital, date: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={newVital.time}
                onChange={(e) =>
                  setNewVital({ ...newVital, time: e.target.value })
                }
              />
            </Grid>

            {/* Weight and Height */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Weight (lbs)"
                type="number"
                value={newVital.weight}
                onChange={(e) =>
                  setNewVital({ ...newVital, weight: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Height (inches)"
                type="number"
                value={newVital.height}
                onChange={(e) =>
                  setNewVital({ ...newVital, height: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="BMI"
                type="number"
                value={newVital.bmi}
                onChange={(e) =>
                  setNewVital({ ...newVital, bmi: e.target.value })
                }
                helperText="Auto-calculated if weight/height entered"
                disabled={newVital.weight && newVital.height}
              />
            </Grid>

            {/* Temperature */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Temperature (°F)"
                type="number"
                step="0.1"
                value={newVital.temperature}
                onChange={(e) =>
                  setNewVital({ ...newVital, temperature: e.target.value })
                }
              />
            </Grid>

            {/* Blood Pressure */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="BP Systolic"
                type="number"
                placeholder="120"
                value={newVital.bloodPressureSystolic}
                onChange={(e) =>
                  setNewVital({
                    ...newVital,
                    bloodPressureSystolic: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="BP Diastolic"
                type="number"
                placeholder="80"
                value={newVital.bloodPressureDiastolic}
                onChange={(e) =>
                  setNewVital({
                    ...newVital,
                    bloodPressureDiastolic: e.target.value,
                  })
                }
              />
            </Grid>

            {/* Heart Rate */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Heart Rate (bpm)"
                type="number"
                value={newVital.heartRate}
                onChange={(e) =>
                  setNewVital({ ...newVital, heartRate: e.target.value })
                }
              />
            </Grid>

            {/* Respiratory Rate */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Respiratory Rate"
                type="number"
                placeholder="breaths/min"
                value={newVital.respiratoryRate}
                onChange={(e) =>
                  setNewVital({
                    ...newVital,
                    respiratoryRate: e.target.value,
                  })
                }
              />
            </Grid>

            {/* Oxygen Saturation */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="O₂ Saturation (%)"
                type="number"
                placeholder="98"
                value={newVital.oxygenSaturation}
                onChange={(e) =>
                  setNewVital({
                    ...newVital,
                    oxygenSaturation: e.target.value,
                  })
                }
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                placeholder="Any observations or notes..."
                value={newVital.notes}
                onChange={(e) =>
                  setNewVital({ ...newVital, notes: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddVital}
                fullWidth
              >
                Record Vitals
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Vitals History */}
      {(!data || data.length === 0) && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No vital signs recorded yet
          </Typography>
        </Box>
      )}

      {data && data.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Vitals History ({data.length} records)
          </Typography>

          {/* Desktop Table View */}
          <TableContainer
            component={Paper}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell>
                    <strong>Date/Time</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Weight</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Height</strong>
                  </TableCell>
                  <TableCell>
                    <strong>BMI</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Temp</strong>
                  </TableCell>
                  <TableCell>
                    <strong>BP</strong>
                  </TableCell>
                  <TableCell>
                    <strong>HR</strong>
                  </TableCell>
                  <TableCell>
                    <strong>O₂</strong>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedVitals.map((vital) => {
                  const bpStatus = getBloodPressureStatus(
                    vital.bloodPressureSystolic,
                    vital.bloodPressureDiastolic
                  );
                  const bmiStatus = getBMIStatus(vital.bmi);

                  return (
                    <TableRow key={vital.id}>
                      <TableCell>
                        {new Date(vital.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {vital.weight ? `${vital.weight} lbs` : "-"}
                      </TableCell>
                      <TableCell>
                        {vital.height ? `${vital.height}"` : "-"}
                      </TableCell>
                      <TableCell>
                        {vital.bmi && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {vital.bmi}
                            {bmiStatus && (
                              <Chip
                                label={bmiStatus.label}
                                size="small"
                                color={bmiStatus.color}
                              />
                            )}
                          </Box>
                        )}
                        {!vital.bmi && "-"}
                      </TableCell>
                      <TableCell>
                        {vital.temperature ? `${vital.temperature}°F` : "-"}
                      </TableCell>
                      <TableCell>
                        {vital.bloodPressureSystolic &&
                        vital.bloodPressureDiastolic ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {vital.bloodPressureSystolic}/
                            {vital.bloodPressureDiastolic}
                            {bpStatus && (
                              <Chip
                                label={bpStatus.label}
                                size="small"
                                color={bpStatus.color}
                              />
                            )}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {vital.heartRate ? `${vital.heartRate} bpm` : "-"}
                      </TableCell>
                      <TableCell>
                        {vital.oxygenSaturation
                          ? `${vital.oxygenSaturation}%`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveVital(vital.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {sortedVitals.map((vital) => (
              <Card key={vital.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {new Date(vital.timestamp).toLocaleString()}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveVital(vital.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Grid container spacing={1}>
                    {vital.weight && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Weight
                        </Typography>
                        <Typography variant="body2">
                          {vital.weight} lbs
                        </Typography>
                      </Grid>
                    )}
                    {vital.height && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Height
                        </Typography>
                        <Typography variant="body2">{vital.height}"</Typography>
                      </Grid>
                    )}
                    {vital.bmi && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          BMI
                        </Typography>
                        <Typography variant="body2">{vital.bmi}</Typography>
                      </Grid>
                    )}
                    {vital.bloodPressureSystolic &&
                      vital.bloodPressureDiastolic && (
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Blood Pressure
                          </Typography>
                          <Typography variant="body2">
                            {vital.bloodPressureSystolic}/
                            {vital.bloodPressureDiastolic}
                          </Typography>
                        </Grid>
                      )}
                    {vital.heartRate && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Heart Rate
                        </Typography>
                        <Typography variant="body2">
                          {vital.heartRate} bpm
                        </Typography>
                      </Grid>
                    )}
                    {vital.oxygenSaturation && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          O₂ Saturation
                        </Typography>
                        <Typography variant="body2">
                          {vital.oxygenSaturation}%
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {vital.notes && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Notes:
                      </Typography>
                      <Typography variant="body2">{vital.notes}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VitalsTab;
